// Data hooks for the fleet screens (dashboard tiles / OLT list / router
// list) + an SSE wiring helper so each screen stays live: when the
// relevant Redis channel publishes (a poll cycle, an issue recompute),
// the corresponding query invalidates and re-fetches.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import { useEventStream } from "@/lib/sse";
import { useRole } from "@/features/auth/roles";

export function useDashboardTiles() {
  return useQuery({
    queryKey: qk.fleet.tiles(),
    queryFn: () => api.fleet.tiles(),
    refetchInterval: 60_000, // safety net; SSE drives the fast path
  });
}

/** Navbar daemon-health widget — keeps itself live by re-fetching on
 *  every routers/olts SSE event (the heartbeat moves on each poll cycle).
 *  Mounted once, in the app layout's topbar. */
export function usePollerWidget() {
  const qc = useQueryClient();
  useEventStream(["routers", "olts"], () => {
    void qc.invalidateQueries({ queryKey: qk.fleet.poller() });
  });
  return useQuery({
    queryKey: qk.fleet.poller(),
    queryFn: () => api.fleet.poller(),
    refetchInterval: 60_000,
  });
}

export function useOlts() {
  return useQuery({
    queryKey: qk.fleet.olts(),
    queryFn: () => api.fleet.olts(),
    refetchInterval: 90_000,
  });
}

export function useRouters() {
  return useQuery({
    queryKey: qk.fleet.routers(),
    queryFn: () => api.fleet.routers(),
    refetchInterval: 60_000,
  });
}

/** Subscribe to the fleet SSE channels and invalidate the matching
 *  queries on each event. Mount once per fleet screen. */
export function useFleetLiveUpdates(opts: { tiles?: boolean; olts?: boolean; routers?: boolean }) {
  const qc = useQueryClient();
  // Reseller-role logins get a restricted /stream — only the OLT-health
  // aggregates (events:routers carries fleet-wide session usernames, so the
  // server drops it for them). So subscribe to just olts/oltissues; the
  // routers list / tiles still re-fetch on those + their polling interval.
  const isReseller = useRole() === "reseller";
  const channels: string[] = [];
  if (isReseller) {
    if (opts.tiles || opts.olts || opts.routers) channels.push("olts", "oltissues");
  } else {
    if (opts.tiles || opts.routers) channels.push("routers");
    if (opts.tiles || opts.olts) channels.push("olts", "oltissues");
  }
  useEventStream(channels, (ev) => {
    if (ev.channel === "routers") {
      if (opts.routers) void qc.invalidateQueries({ queryKey: qk.fleet.routers() });
      if (opts.tiles) void qc.invalidateQueries({ queryKey: qk.fleet.tiles() });
    } else if (ev.channel === "olts" || ev.channel === "oltissues") {
      if (opts.olts) void qc.invalidateQueries({ queryKey: qk.fleet.olts() });
      if (opts.tiles) void qc.invalidateQueries({ queryKey: qk.fleet.tiles() });
    }
  });
}
